// composables/useWordPress.js
import { useRuntimeConfig, useState } from '#imports'

/** ===== TAG GLOBAL (un solo tag para todos los idiomas) ===== */
const DEFAULT_TAG_SLUG = 'tvm' // 👈 cámbialo si necesitas otro

function normalizeTagSlug(s) {
  const v = String(s || '').trim().toLowerCase()
  return v.startsWith('tag-') ? v.slice(4) : v
}

function useGlobalTag() {
  const tagSlug = useState('wp:globalTagSlug', () => DEFAULT_TAG_SLUG)
  return tagSlug
}

/** Cambiar el tag en runtime */
export function setTagSlug(slug) {
  const tag = useGlobalTag()
  tag.value = normalizeTagSlug(slug) || DEFAULT_TAG_SLUG
  wpCache.postsListByKey.clear()
}

/** Obtener el tag actual */
export function getTagSlug() {
  return normalizeTagSlug(useGlobalTag().value || DEFAULT_TAG_SLUG)
}

/** Normaliza idiomas app -> WP */
function mapAppToWp(code = 'en') {
  const c = String(code || '').toLowerCase()
  if (c.startsWith('pt-br')) return 'pt-br'
  if (c.startsWith('es')) return 'es'
  if (c.startsWith('ko')) return 'ko'
  if (c.startsWith('zh')) return 'zh'
  if (c.startsWith('ja')) return 'ja'
  return 'en'
}

/** Reducimos el post para listas */
function minimizePostForList(p) {
  if (!p) return null
  return {
    id: p.id,
    slug: p.slug,
    title: p.title,
    content: p.content,
    excerpt: p.excerpt,
    categories: p.categories || [],
    tags: p.tags || [],
    class_list: p.class_list || [],
    yoast_head_json: p.yoast_head_json || {},
    translations: p.translations || null
  }
}

/** LocalStorage helpers */
function safeLocalGet(key) {
  try {
    if (process.client && window?.localStorage) {
      const raw = localStorage.getItem(key)
      return raw ? JSON.parse(raw) : null
    }
  } catch {}
  return null
}
function safeLocalSet(key, val) {
  try {
    if (process.client && window?.localStorage) {
      localStorage.setItem(key, JSON.stringify(val))
    }
  } catch {}
}

/** URL builder */
function buildUrl(base, path, params = {}) {
  const cleanBase = base.endsWith('/') ? base : base + '/'
  const cleanPath = path.replace(/^\//, '')
  const url = new URL(cleanPath, cleanBase)
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') {
      url.searchParams.set(k, String(v))
    }
  })
  return url.toString()
}

/** Fetch JSON con de-dup inflight */
async function fetchJson(url, inflight, key) {
  if (inflight.has(key)) return inflight.get(key)
  const p = fetch(url)
    .then(async (r) => {
      if (!r.ok) {
        const tx = await r.text().catch(() => '')
        throw new Error(`HTTP ${r.status}: ${r.statusText} :: ${tx}`)
      }
      return r.json()
    })
    .finally(() => inflight.delete(key))
  inflight.set(key, p)
  return p
}

/** ===== CACHÉ ===== */
const wpCache = {
  inflight: new Map(),
  catsByLang: new Map(),
  postsListByKey: new Map(),
  postDetailByKey: new Map(),
  tagIdByLangAndSlug: new Map(),
}

function makeListKey(wpLang, filters = {}) {
  const sig = Object.entries(filters)
    .filter(([_, v]) => v !== undefined && v !== null && v !== '')
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([k, v]) => `${k}=${Array.isArray(v) ? v.join(',') : v}`)
    .join('&')
  return `${wpLang}::${sig || 'all'}`
}

/** helper: check class_list */
function hasTagClass(post, tagSlug) {
  const want = `tag-${normalizeTagSlug(tagSlug)}`
  return Array.isArray(post?.class_list) && post.class_list.includes(want)
}

/** --------- RESOLVER ID DEL TAG --------- */
async function fetchTagIdBySlug(lang = 'en', tagSlug) {
  const slug = normalizeTagSlug(tagSlug)
  if (!slug) return null
  const wpLang = mapAppToWp(lang)
  const cacheKey = `${wpLang}:${slug}`
  if (wpCache.tagIdByLangAndSlug.has(cacheKey)) return wpCache.tagIdByLangAndSlug.get(cacheKey)

  // 1) exacto por slug + lang
  let url = buildUrl(wpBase, '/wp-json/wp/v2/tags', { slug, lang: wpLang, per_page: 1, _fields: 'id,slug,translations' })
  try {
    const data = await fetchJson(url, wpCache.inflight, `tagId:${cacheKey}:lang`)
    if (Array.isArray(data) && data.length && data[0]?.id) {
      const id = Number(data[0].id)
      wpCache.tagIdByLangAndSlug.set(cacheKey, id)
      return id
    }
  } catch {}

  // 2) sin lang + translations
  url = buildUrl(wpBase, '/wp-json/wp/v2/tags', { slug, per_page: 1, _fields: 'id,slug,translations' })
  try {
    const data = await fetchJson(url, wpCache.inflight, `tagId:${cacheKey}:nol`)
    if (Array.isArray(data) && data.length) {
      const term = data[0]
      const transId = term?.translations?.[wpLang]
      const id = Number(transId || term?.id || 0) || null
      if (id) {
        wpCache.tagIdByLangAndSlug.set(cacheKey, id)
        return id
      }
    }
  } catch {}

  // 3) búsqueda
  url = buildUrl(wpBase, '/wp-json/wp/v2/tags', { search: slug, lang: wpLang, per_page: 10, _fields: 'id,slug,translations' })
  try {
    const data = await fetchJson(url, wpCache.inflight, `tagId:${cacheKey}:search`)
    if (Array.isArray(data) && data.length) {
      const hit = data.find(t => String(t?.slug).toLowerCase() === slug) || data[0]
      const id = Number(hit?.translations?.[wpLang] || hit?.id || 0) || null
      if (id) {
        wpCache.tagIdByLangAndSlug.set(cacheKey, id)
        return id
      }
    }
  } catch {}

  return null
}

/** --------- LISTA GENÉRICA DE POSTS --------- */
async function fetchAllPosts(lang = 'en', { maxPages = 3, perPage = 20, extraParams = {} } = {}) {
  const wpLang = mapAppToWp(lang)
  const listKey = makeListKey(wpLang, { lang: wpLang, ...extraParams, perPage, maxPages })
  if (wpCache.postsListByKey.has(listKey)) return wpCache.postsListByKey.get(listKey)

  const all = []
  for (let page = 1; page <= maxPages; page++) {
    const url = buildUrl(wpBase, '/wp-json/wp/v2/posts', {
      per_page: perPage,
      page,
      lang: wpLang,
      _embed: '1',
      ...extraParams
    })
    const data = await fetchJson(url, wpCache.inflight, `posts:list:${listKey}:p${page}`).catch(() => [])
    if (!Array.isArray(data)) break
    const list = data.map(minimizePostForList).filter(Boolean)
    all.push(...list)
    if (list.length < perPage) break
  }
  wpCache.postsListByKey.set(listKey, all)
  return all
}

/** --------- POSTS SOLO DEL TAG GLOBAL --------- */
async function fetchPostsOnlyTVM(lang = 'en', opts = { maxPages: 3, perPage: 20 }) {
  const slug = getTagSlug()
  const wpLang = mapAppToWp(lang)
  const tagId = await fetchTagIdBySlug(lang, slug)

  // A) con lang + tagId
  if (tagId) {
    const a = await fetchAllPosts(lang, {
      maxPages: opts?.maxPages ?? 3,
      perPage: opts?.perPage ?? 20,
      extraParams: { tags: tagId }
    })
    if (a?.length) return a

    // B) solo tagId
    const all = []
    for (let page = 1; page <= (opts?.maxPages ?? 3); page++) {
      const url = buildUrl(wpBase, '/wp-json/wp/v2/posts', {
        per_page: opts?.perPage ?? 20,
        page,
        _embed: '1',
        tags: tagId
      })
      const data = await fetchJson(url, wpCache.inflight, `posts:list:tag-${tagId}:p${page}`).catch(() => [])
      if (!Array.isArray(data)) break
      const list = data.map(minimizePostForList).filter(Boolean)
      all.push(...list)
      if (list.length < (opts?.perPage ?? 20)) break
    }
    if (all.length) return all
  }

  // C) fallback: client-side por class_list
  const raw = await fetchAllPosts(lang, { maxPages: opts?.maxPages ?? 3, perPage: opts?.perPage ?? 20 })
  return raw.filter(p => hasTagClass(p, slug))
}

/** --------- DETALLE POR SLUG --------- */
function getPostBySlugFromLocalStorage(slug, lang = 'en') {
  const wpLang = mapAppToWp(lang)
  if (!process.client) return null
  const detail = safeLocalGet(`wp:post:detail:${wpLang}:${slug}`)
  if (detail) return detail
  const fromList = safeLocalGet(`wp:post:list:${wpLang}:${slug}`)
  return fromList || null
}

async function fetchPostBySlug(slug, lang = 'en') {
  const cleanSlug = (slug || '').trim()
  if (!cleanSlug) return null
  const wpLang = mapAppToWp(lang)
  const cacheKey = `${wpLang}:${cleanSlug}`
  if (wpCache.postDetailByKey.has(cacheKey)) return wpCache.postDetailByKey.get(cacheKey)

  let result = getPostBySlugFromLocalStorage(cleanSlug, lang)
  if (!result || !result?.content?.rendered) {
    try {
      const url = buildUrl(wpBase, '/wp-json/wp/v2/posts', {
        slug: cleanSlug, lang: wpLang, _embed: '1'
      })
      const data = await fetchJson(url, wpCache.inflight, `post:slug:${wpLang}:${cleanSlug}`)
      const first = Array.isArray(data) ? data[0] : data
      if (first) {
        result = first
        wpCache.postDetailByKey.set(cacheKey, result)
        if (process.client) safeLocalSet(`wp:post:detail:${wpLang}:${first.slug}`, first)
      }
    } catch {}
  }
  return result || null
}

/** --------- DETALLE POR ID --------- */
async function fetchPostById(id, lang = 'en') {
  if (!id) return null
  const wpLang = mapAppToWp(lang)
  for (const [k, v] of wpCache.postDetailByKey.entries()) {
    if (v?.id === Number(id)) return v
  }
  try {
    const url = buildUrl(wpBase, `/wp-json/wp/v2/posts/${id}`, { lang: wpLang, _embed: '1' })
    const data = await fetchJson(url, wpCache.inflight, `post:id:${wpLang}:${id}`)
    if (data && data.slug) {
      const cacheKey = `${wpLang}:${data.slug}`
      wpCache.postDetailByKey.set(cacheKey, data)
      if (process.client) safeLocalSet(`wp:post:detail:${wpLang}:${data.slug}`, data)
      return data
    }
  } catch {}
  return null
}

/** --------- TRADUCCIONES --------- */
async function fetchTranslatedPost(currentPost, targetLocale) {
  if (!currentPost?.translations) return null
  const targetId = currentPost.translations[targetLocale]
  if (!targetId) return null
  return await fetchPostById(targetId, targetLocale)
}

async function resolveTranslatedCategorySlug(currentCategorySlug, currentLocale, targetLocale, targetPost) {
  try {
    const [currentCats, targetCats] = await Promise.all([
      fetchCategories(currentLocale),
      fetchCategories(targetLocale)
    ])
    const currentCat = currentCats.find(c => String(c.slug) === String(currentCategorySlug))
    if (!currentCat) return currentCategorySlug
    const targetCatId = currentCat.translations?.[targetLocale]
    if (targetCatId) {
      const targetCat = targetCats.find(c => Number(c.id) === Number(targetCatId))
      if (targetCat) return targetCat.slug
    }
    if (targetPost?.categories?.length) {
      const targetCat = targetCats.find(c => targetPost.categories.includes(c.id))
      if (targetCat) return targetCat.slug
    }
    return currentCategorySlug
  } catch {
    return currentCategorySlug
  }
}

/** --------- CATEGORÍAS --------- */
async function fetchCategories(lang = 'en') {
  const wpLang = mapAppToWp(lang)
  if (wpCache.catsByLang.has(wpLang)) return wpCache.catsByLang.get(wpLang)
  const url = buildUrl(wpBase, '/wp-json/wp/v2/categories', { per_page: 100, lang: wpLang })
  try {
    const data = await fetchJson(url, wpCache.inflight, `cats:${wpLang}`)
    const arr = Array.isArray(data) ? data : []
    wpCache.catsByLang.set(wpLang, arr)
    return arr
  } catch {
    return []
  }
}

export function useWordPress() {
  const runtimePublic = useRuntimeConfig().public || {}
  globalThis.wpBase = runtimePublic.wpBase || 'https://testsurfsup.tvmarkets.com'

  return {
    // Base
    getWpBase: () => wpBase,
    mapAppToWp,

    // Tag global
    getTagSlug,
    setTagSlug,

    // Taxonomías
    fetchCategories,

    // Posts
    fetchAllPosts,
    fetchPostsOnlyTVM,
    fetchPostBySlug,
    fetchPostById,
    getPostBySlugFromLocalStorage,

    // Traducciones
    fetchTranslatedPost,
    resolveTranslatedCategorySlug,
  }
}
